import { CatalogService } from '@services/data/catalog.service';

/** Reglas de siniestro (Art. VII / XI) leídas del catálogo del proceso. */
export const CLAIM_RULE = {
  MAX_DETAILS: 'CLAIM_MAX_DETAILS',
  MINOR_MAX_PCT: 'CLAIM_MINOR_MAX_PCT',
  MINOR_MAX_COUNT: 'CLAIM_MINOR_MAX_COUNT',
  MAX_REPORTS_YEAR: 'CLAIM_MAX_REPORTS_YEAR',
  REPORT_MAX_DAYS: 'CLAIM_REPORT_MAX_DAYS',
  NOTICE_DAYS: 'CLAIM_NOTICE_DAYS',
  FMR_VALUE: 'FMR_VALUE'
} as const;

export type CanAddDetailResult = {
  ok: boolean;
  reason?: string;
};

function processRuleMap(catalog: CatalogService, processSlug: string | null | undefined): string[] {
  if (!processSlug) return [];
  const proc = (catalog.processes() ?? []).find(
    (p: any) => String(p.slug || p.id || '').toUpperCase() === String(processSlug).toUpperCase()
  );
  const map = proc?.ruleMap ?? proc?.rule_map;
  return Array.isArray(map) ? map.map((s: any) => String(s).trim()).filter(Boolean) : [];
}

/** Regla activa del proceso si el slug está en ruleMap; si no hay map, permite lookup global. */
export function processRuleValue(
  catalog: CatalogService,
  processSlug: string | null | undefined,
  ruleSlug: string,
  fallback?: number
): number | undefined {
  const map = processRuleMap(catalog, processSlug);
  if (map.length && !map.some((s) => s.toUpperCase() === ruleSlug.toUpperCase())) {
    return fallback;
  }
  const rule = catalog.getRuleBySlug(ruleSlug);
  if (!rule || rule.isActive === false) return fallback;
  const n = Number(rule.value);
  return Number.isFinite(n) ? n : fallback;
}

export function fmrAmount(catalog: CatalogService, partyFmr?: number | null): number {
  if (partyFmr != null && Number(partyFmr) > 0) return Number(partyFmr);
  return processRuleValue(catalog, null, CLAIM_RULE.FMR_VALUE, 1285000) ?? 1285000;
}

export function noticeMaxDays(catalog: CatalogService, processSlug?: string | null): number {
  return processRuleValue(catalog, processSlug, CLAIM_RULE.NOTICE_DAYS, 10) ?? 10;
}

export function reportMaxDays(catalog: CatalogService, processSlug?: string | null): number {
  return processRuleValue(catalog, processSlug, CLAIM_RULE.REPORT_MAX_DAYS, 60) ?? 60;
}

export function minorMaxPct(catalog: CatalogService, processSlug?: string | null): number {
  return processRuleValue(catalog, processSlug, CLAIM_RULE.MINOR_MAX_PCT, 25) ?? 25;
}

/** Clasifica un monto como daño menor (MINOR) o mayor (MAJOR). */
export function classifyClaimCost(
  totalCost: number,
  fmrValue: number = 1285000,
  minorMaxPctVal: number = 25
): 'MINOR' | 'MAJOR' {
  const threshold = (Number(fmrValue) * Number(minorMaxPctVal)) / 100;
  return Number(totalCost || 0) <= threshold ? 'MINOR' : 'MAJOR';
}

/**
 * Valida si se puede agregar otra línea de daño al informe.
 * 1) Restricciones de proceso (pérdida total, terceros, max_details).
 * 2) Acumulación de menores: máx 3 líneas menores (≤ 25% FMR).
 * 3) Si el informe ya contiene un daño mayor: bloquea acumulación de otros daños.
 */
export function canAddDetail(opts: {
  catalog: CatalogService;
  processSlug: string | null | undefined;
  details: any[];
  isTotalLoss?: boolean;
  partyFmr?: number | null;
}): CanAddDetailResult {
  const { catalog, processSlug, details } = opts;
  const list = details ?? [];
  const maxDetails = processRuleValue(catalog, processSlug, CLAIM_RULE.MAX_DETAILS);
  if (maxDetails != null && list.length >= maxDetails) {
    return { ok: false, reason: `Este proceso permite máximo ${maxDetails} daño(s).` };
  }
  if (opts.isTotalLoss && list.length >= 1 && maxDetails == null) {
    return { ok: false, reason: 'En Pérdida Total solo se permite 1 detalle.' };
  }
  if (String(processSlug || '').toUpperCase() === 'ACCIDENT_THIRD_PARTY' && list.length >= 1 && maxDetails == null) {
    return { ok: false, reason: 'En siniestro a terceros solo se permite 1 detalle.' };
  }
  if (String(processSlug || '').toUpperCase() === 'ACCIDENT' && list.length > 0) {
    const fmr = fmrAmount(catalog, opts.partyFmr);
    const pct = minorMaxPct(catalog, processSlug);
    const threshold = (fmr * pct) / 100;
    const hasMajor = list.some((d) => Number(d.totalCost ?? 0) > threshold);
    if (hasMajor) {
      return { ok: false, reason: 'Un siniestro mayor no permite acumular daños adicionales en el mismo informe.' };
    }
    const maxMinor = processRuleValue(catalog, processSlug, CLAIM_RULE.MINOR_MAX_COUNT, 3) ?? 3;
    if (list.length >= maxMinor) {
      return { ok: false, reason: `Se alcanzó el máximo de ${maxMinor} daños menores acumulables.` };
    }
  }
  return { ok: true };
}

/** Valida si un costo de línea puede tratarse como menor acumulable (≤ % FMR). */
export function isMinorDetailCost(cost: number, fmr: number, pct: number): boolean {
  if (!(fmr > 0)) return true;
  return Number(cost) <= fmr * (pct / 100);
}

export function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const lossDate = new Date(dateStr);
  if (Number.isNaN(lossDate.getTime())) return null;
  return Math.floor((Date.now() - lossDate.getTime()) / (1000 * 3600 * 24));
}
